
import { vertexShaderSource, fragmentShaderSource } from './shaders';
import { AudioFeatures, EngineParams } from '../types';
import { BAYER_MATRIX_4X4 } from '../constants';

export class AxiomRenderer {
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private positionBuffer: WebGLBuffer;
  private uniforms: { [key: string]: WebGLUniformLocation } = {};

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { 
      antialias: false, 
      preserveDrawingBuffer: true,
      alpha: false,
      depth: false,
      stencil: false
    });
    if (!gl) throw new Error('WebGL2 not supported');
    this.gl = gl;

    const vs = this.createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fs = this.createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);
    this.program = this.createProgram(vs, fs);
    gl.useProgram(this.program);

    this.positionBuffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const posLoc = gl.getAttribLocation(this.program, 'aPosition');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uniformNames = ['uTime', 'uResolution', 'uAudio', 'uParams', 'uBayer'];
    uniformNames.forEach(name => {
      const loc = gl.getUniformLocation(this.program, name);
      if (loc) this.uniforms[name] = loc;
    });
  }

  private createShader(type: number, source: string): WebGLShader {
    const shader = this.gl.createShader(type)!;
    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      const info = this.gl.getShaderInfoLog(shader);
      console.error('Shader compile error:', info);
      throw new Error(`Shader compilation failed: ${info}`);
    }
    return shader;
  }

  private createProgram(vs: WebGLShader, fs: WebGLShader): WebGLProgram {
    const program = this.gl.createProgram()!;
    this.gl.attachShader(program, vs);
    this.gl.attachShader(program, fs);
    this.gl.linkProgram(program);
    if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
      const info = this.gl.getProgramInfoLog(program);
      console.error('Program link error:', info);
      throw new Error(`Program link failed: ${info}`);
    }
    return program;
  }

  public getCanvas(): HTMLCanvasElement {
    return this.gl.canvas as HTMLCanvasElement;
  }

  public resize(width: number, height: number) {
    this.gl.viewport(0, 0, width, height);
  }

  public render(time: number, audio: AudioFeatures, params: EngineParams) {
    const gl = this.gl;
    gl.useProgram(this.program);

    if (this.uniforms.uTime) gl.uniform1f(this.uniforms.uTime, time);
    if (this.uniforms.uResolution) gl.uniform2f(this.uniforms.uResolution, gl.canvas.width, gl.canvas.height);

    const audioArr = new Float32Array([
      audio.sub, audio.bass, audio.lowMid, audio.highMid,
      audio.high, audio.rms, audio.centroid, audio.transient
    ]);
    if (this.uniforms.uAudio) gl.uniform1fv(this.uniforms.uAudio, audioArr);

    const paramsArr = new Float32Array([
      params.topology, params.topX, params.topY, params.topZ, params.scale, params.grid, params.symmetry, params.shapeComplexity, params.wireframe,
      params.drive, params.inertia, params.impact, params.oscillation,
      params.warp, params.fold, params.quantize, params.resonance, params.scatter, params.fractal,
      params.ambientLight, params.directionalLight, params.pointLight, params.lightColor, params.bloom, params.roughness, params.metalness, params.emission,
      params.grain, params.chroma, params.color1, params.color2, params.depth, params.chromaticAberration, params.vignette, params.noise, params.stability
    ]);
    if (this.uniforms.uParams) gl.uniform1fv(this.uniforms.uParams, paramsArr);
    
    if (this.uniforms.uBayer) gl.uniform1fv(this.uniforms.uBayer, new Float32Array(BAYER_MATRIX_4X4));

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
}
